import React, { useState, useEffect } from 'react';
import { useForm, ValidationError } from '@formspree/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

const MiniTrialForm = ({ isOpen, onClose }) => {
  const [formspreeState, formspreeHandleSubmit] = useForm("xnqwevvn");
  const [fullName, setFullName] = useState('');
  const [nameError, setNameError] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');

  useEffect(() => {
    if (formspreeState.succeeded && isOpen) {
      setFullName('');
      setNameError('');
      setPhoneNumber('');
      setPhoneError('');
      // Optional: Auto-close after a few seconds
    }
  }, [formspreeState.succeeded, isOpen]); // Removed onClose from dependencies as it might cause loop if not memoized

  const validateFullName = (name) => {
    if (!name.trim()) {
      return 'Full name is required.';
    }
    if (name.trim().split(' ').length < 2) {
      return 'Please enter at least your first and last name.';
    }
    return '';
  };

  const validatePhoneNumber = (number) => {
    if (!number) {
      return 'Phone number is required.';
    }
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(number)) {
      return 'Phone number must be exactly 10 digits.';
    }
    return '';
  };

  const handleNameChange = (e) => {
    const newName = e.target.value;
    setFullName(newName);
    if (newName || nameError) {
        setNameError(validateFullName(newName));
    }
  };

  const handlePhoneChange = (e) => {
    const newNumber = e.target.value.replace(/[^\d]/g, '');
    setPhoneNumber(newNumber);
    if (newNumber || phoneError) {
        setPhoneError(validatePhoneNumber(newNumber));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const currentNameError = validateFullName(fullName);
    const currentPhoneError = validatePhoneNumber(phoneNumber);

    if (currentNameError) {
      setNameError(currentNameError);
    }
    if (currentPhoneError) {
      setPhoneError(currentPhoneError);
    }

    if (currentNameError || currentPhoneError) {
      return; // Prevent submission if any validation fails
    }
    
    formspreeHandleSubmit(e);
  };
  
  const handleClose = () => {
    if (!formspreeState.succeeded) {
        setFullName('');
        setNameError('');
        setPhoneNumber('');
        setPhoneError('');
    }
    onClose();
  };

  return (
    <div 
      className={`fixed bottom-0 right-4 w-full max-w-md z-[60] transition-transform duration-300 ease-in-out transform ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
    >
      <div className="bg-white p-6 rounded-l-lg shadow-xl w-full relative">
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-xl z-10"
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>

        {formspreeState.succeeded ? (
          <div className="text-center">
            <h3 className="text-2xl font-semibold text-emerald-600 mb-4">Thanks for your submission!</h3>
            <p className="text-gray-700 mb-6">We'll be in touch soon.</p>
            <button
              onClick={handleClose} // Already uses handleClose
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <h3 className="text-2xl font-semibold text-gray-900 mb-2">Request a Quick Trial</h3>
            <p className="text-sm text-gray-600 mb-6">
              Fill out your details below and we'll get back to you shortly.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="mini-name" className="block text-sm font-medium text-gray-700">
                  Full Name (First and Last)
                </label>
                <input
                  id="mini-name"
                  name="name"
                  type="text"
                  value={fullName} // Controlled component
                  onChange={handleNameChange} // Custom handler
                  required // Keep for basic browser validation / accessibility
                  className={`mt-1 appearance-none block w-full px-3 py-2 border ${nameError ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                />
                {nameError && (
                  <p className="text-red-500 text-xs mt-1">{nameError}</p>
                )}
              </div>

              <div>
                <label htmlFor="mini-email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  id="mini-email"
                  name="email"
                  type="email"
                  required
                  className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                <ValidationError
                  prefix="Email"
                  field="email"
                  errors={formspreeState.errors}
                  className="text-red-500 text-xs mt-1"
                />
              </div>

              <div>
                <label htmlFor="mini-phone" className="block text-sm font-medium text-gray-700">
                  Phone Number (10 digits)
                </label>
                <input
                  id="mini-phone"
                  name="phone" 
                  type="tel" 
                  value={phoneNumber} 
                  onChange={handlePhoneChange} 
                  required
                  maxLength={10} 
                  className={`mt-1 appearance-none block w-full px-3 py-2 border ${phoneError ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                />
                {phoneError && (
                  <p className="text-red-500 text-xs mt-1">{phoneError}</p>
                )}
                <ValidationError
                    prefix="Phone"
                    field="phone"
                    errors={formspreeState.errors}
                    className="text-red-500 text-xs mt-1"
                />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={formspreeState.submitting}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {formspreeState.submitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default MiniTrialForm; 